import { DATE_TYPES, DatePeriod, Time, TIME_TYPES, TimePeriod } from "@shared/types/period.type";
import { Status, STATUS_TYPES } from "@shared/types/status.types";
import { toDate } from "date-fns";
import { isString } from "es-toolkit";

// eslint-disable-next-line @tseslint/typedef
export const PROJECT_SPECIAL_TIME_TYPES = {
  opening: "opening",
  closing: "closing"
} as const;

const PROJECT_SPECIAL_TIME_TYPE_KEYS: string[] = Object.values(PROJECT_SPECIAL_TIME_TYPES);

export type ProjectSpecialTimeType = typeof PROJECT_SPECIAL_TIME_TYPES[keyof typeof PROJECT_SPECIAL_TIME_TYPES];
export interface ProjectSpecialTime {
  readonly id: string;
  type: ProjectSpecialTimeType;
  note: string;
  timePeriod: TimePeriod;
  datePeriod: DatePeriod;
}
export type ProjectSpecialTimeWithoutId = Omit<ProjectSpecialTime, "id">;
export type ProjectSpecialTimeId = Pick<ProjectSpecialTime, "id">;
export interface TimePeriodOverviewDto {
  fullDay?: boolean | undefined;
  startTime?: string | undefined;
  endTime?: string | undefined;
}
export interface TimePeriodDto {
  fullDay?: boolean | undefined;
  start?: string | undefined;
  end?: string | undefined;
}
/** Base used in both {@link ProjectSpecialTimeOverviewDto} and {@link ProjectSpecialTimeDto} */
export interface ProjectSpecialTimeDtoBase {
  readonly number: string;
  closing?: boolean | undefined;
  date?: string | undefined;
  description?: string | undefined;
}
/**
 * This Type is the one that the Backend expects in their put and post. It's like {@link ProjectSpecialTimeOverviewDto},
 * but the period is a required member instead of being flat in the interface.
 */
export interface ProjectSpecialTimeDto extends ProjectSpecialTimeDtoBase {
  period: TimePeriodDto;
}
/**
 * This Type is the one that the Backend sends in their getAll. It's like {@link ProjectSpecialTimeDto} but the period is flat in the object and some members are named differently.
 */
export type ProjectSpecialTimeOverviewDto = ProjectSpecialTimeDtoBase & TimePeriodOverviewDto;
export type ProjectSpecialTimeDtoWithoutNumber = Omit<ProjectSpecialTimeDto, "number">;
export function parseToTimePeriod (value: TimePeriodOverviewDto): TimePeriod | undefined {
  if (value.fullDay)
    return { type: TIME_TYPES.wholeDay };

  const parsedStartTime: Time | undefined = value.startTime ? Time.formString(value.startTime) : undefined;
  const parsedEndTime: Time | undefined = value.endTime ? Time.formString(value.endTime) : undefined;

  if (parsedStartTime !== undefined && parsedEndTime !== undefined)
    return { type: TIME_TYPES.timespan, startTime: parsedStartTime, endTime: parsedEndTime };

  return undefined;
}
// TODO: Backend-support for timespan?
export function parseToDatePeriod (value: string | undefined): DatePeriod | undefined {
  const parsedDate: Date | undefined = value ? toDate(value) : undefined;

  if (!parsedDate) return undefined;

  return { type: DATE_TYPES.single, date: parsedDate };
}
/**
 * Parses the Response type of the get which is {@link ProjectSpecialTimeOverviewDto} to the type used for the client {@link ProjectSpecialTime}
 * @param value the object to parse
 * @returns the parsed object
 */
export function parseToProjectSpecialTime (value: ProjectSpecialTimeOverviewDto): ProjectSpecialTime | undefined {
  const timePeriod: TimePeriod | undefined = parseToTimePeriod(value);
  const datePeriod: DatePeriod | undefined = parseToDatePeriod(value.date);

  if (!timePeriod || !datePeriod)
    return;

  const type: ProjectSpecialTimeType = value.closing ? PROJECT_SPECIAL_TIME_TYPES.closing : PROJECT_SPECIAL_TIME_TYPES.opening;

  return {
    type,
    id: value.number,
    timePeriod,
    datePeriod,
    note: value.description ?? ""
  };
}
export function parseToProjectSpecialTimeStatus (value: ProjectSpecialTimeType): Status {
  return {
    statusType: value === PROJECT_SPECIAL_TIME_TYPES.opening ? STATUS_TYPES.success : STATUS_TYPES.neutral,
    displayName: ""
  };
}

function parseToProjectTimePeriodDto (value: TimePeriod): TimePeriodDto {
  return {
    fullDay: value.type === TIME_TYPES.wholeDay,
    start: value.type === TIME_TYPES.timespan ? value.startTime.toIsoTimeString() : undefined,
    end: value.type === TIME_TYPES.timespan ? value.endTime.toIsoTimeString() : undefined
  };
}

/**
 * Parses the client object from type {@link ProjectSpecialTime | ProjectSpecialTime} to type {@link ProjectSpecialTimeDtoWithoutNumber | ProjectSpecialTimeDtoWithoutNumber} which is required by the backend.
 * @param value The object to parse
 * @returns the parsed object
 */
export function parseToProjectSpecialTimeDto (value: ProjectSpecialTimeWithoutId): ProjectSpecialTimeDtoWithoutNumber {
  return {
    description: value.note,
    date: value.datePeriod.type === DATE_TYPES.timespan ? value.datePeriod.startDate.toISOStringDate(true) : value.datePeriod.date.toISOStringDate(true),
    closing: value.type === PROJECT_SPECIAL_TIME_TYPES.closing,
    period: parseToProjectTimePeriodDto(value.timePeriod)
  };
}
export function isProjectSpecialTimeType (value: unknown): value is ProjectSpecialTimeType {
  if (!value) return false;

  if (isString(value) && PROJECT_SPECIAL_TIME_TYPE_KEYS.includes(value))
    return true;

  return false;
}
